'use client';

import { KlineFidelity, useTokenKline } from '@/hooks/use-token-kline';
import {
  generateFidelity,
  getCryptoName,
  getRecurrence,
  crypto2Symbol,
} from '@/components/predic/detail/market/crypto-utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import { useTheme } from 'next-themes';
import { useRightDrawer } from '@/contexts/DrawerContext';
import { useTranslation } from '@/lib/i18n';
import { useNotificationStore } from '@/stores/notificationStore';
import { useFixedChatStore } from '@/stores/fixedChatStore';
import { currencyFormatter } from '@/lib/utils';
import ResolvedButtons from './ResolvedButton';
import { BaseChartOptions, grid, tooltip, xAxis, yAxis } from './BaseChartConfig';
import {
  parseSlug2EndTimestamp,
  parseSlug2StartTimestamp,
  recurrenceToMs,
} from '../detail/market/util';
import { EventData, PolymarketMarket } from '../detail/market/types';
import { orderBy } from 'lodash';
import EChart from '@/components/common/EChart';
import { formatEndTime } from '../hooks/use-time-window-list';

// 必須先加載插件
dayjs.extend(utc);
dayjs.extend(timezone);

const probabilityColors = [
  '#47337D', // 0% 0
  '#262B31', // 0–10% 1
  '#2E343B', // 11–20% 2
  '#373E46', // 21–30% 3

  '#414851', // 31–40% 4
  '#4C545E', // 41–50% 5
  '#585F6A', // 51–60% 6
  '#656D78', // 61–70% 7

  '#737B86', // 71–80% 8
  '#828A95', // 81–90% 9
  '#929AA5', // 91–99% 10
  '#A3ABB5', // 100% 10
];

const MARK_AREA_PRICE_PADDING_RATIO = 0.08;

interface MarkAreaMeta {
  lowerBoundary: number;
  upperBoundary: number;
  outcomeYesPrice: string;
}

interface CryptoPricePoint {
  timestampMs: number;
  price: number;
}

export default function CryptoMultipleTrendChart({
  eventData,
  openInSidebar,
}: {
  eventData: EventData;
  openInSidebar?: boolean;
}) {
  const cryptoName = getCryptoName(eventData.tags);
  const recurrence = getRecurrence(eventData.tags);

  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const verticalGuideRef = useRef<HTMLDivElement>(null);
  const cursorPointRef = useRef<HTMLDivElement>(null);
  const linePointRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const { isOpen } = useRightDrawer();
  const { t } = useTranslation();
  const { chatId } = useFixedChatStore();
  const { isOpen: isNotificationOpen } = useNotificationStore();

  const endTime = parseSlug2EndTimestamp(eventData?.event?.slug, recurrence) / 1000;
  const startTime = endTime - recurrenceToMs[recurrence] / 1000;
  const fidelity = generateFidelity(recurrence);
  const cryptoSymbol = crypto2Symbol[cryptoName];

  const currentWindow = useMemo(
    () => ({
      slug: eventData?.event?.slug,
      startTs: dayjs(eventData?.event.start_date).unix(),
      endTs: dayjs(eventData?.event.end_date).unix(),
      label: formatEndTime(dayjs(eventData?.event.end_date).unix()),
      token: cryptoSymbol,
      active: false,
    }),
    [eventData?.event, cryptoSymbol],
  );

  const { data: klineData } = useTokenKline({
    token: cryptoSymbol,
    fidelity: fidelity as KlineFidelity,
    start_time: startTime, //Math.floor(new Date(startDate).getTime() / 1000),
    end_time: endTime, //Math.floor(new Date(endDate).getTime() / 1000),
    resolved:
      !!eventData.event.closed || eventData.markets?.[0]?.uma_resolution_status === 'resolved',
  });

  const filteredMarkets = useMemo(() => {
    if (!eventData.markets) return [];
    if (!eventData.event?.closed) {
      return eventData.markets.filter((m) => m.uma_resolution_status !== 'resolved');
    }
    return eventData.markets;
  }, [eventData.markets]);

  // 准备图表数据
  const markAreaData = useMemo(() => {
    if (!filteredMarkets.length || !klineData?.history?.length) return [];

    const klinePrices = klineData.history
      .map((item) => Number(item.close_price))
      .filter(Number.isFinite);

    if (!klinePrices.length) return [];

    const minKlinePrice = Math.min(...klinePrices);
    const maxKlinePrice = Math.max(...klinePrices);
    const klinePriceRange = Math.max(maxKlinePrice - minKlinePrice, minKlinePrice * 0.01, 1);
    const paddedMinPrice = minKlinePrice - klinePriceRange * MARK_AREA_PRICE_PADDING_RATIO;
    const paddedMaxPrice = maxKlinePrice + klinePriceRange * MARK_AREA_PRICE_PADDING_RATIO;

    const items = orderBy(
      filteredMarkets.map((m) => {
        const yesPrice = parseFloat(m.outcome_yes_price);
        return {
          group_item_title: parseFloat(m.group_item_title.replace(/[^\d]/g, '')),
          yes_price: (parseFloat(m.outcome_yes_price) * 100).toFixed(1),
          umsStatus: m.uma_resolution_status,
          color: `rgba(71, 51, 125, ${yesPrice > 0.9 ? 0 : yesPrice.toFixed(1)})`,
        };
      }),
      'group_item_title',
    ).filter(
      (item) => item.group_item_title >= paddedMinPrice && item.group_item_title <= paddedMaxPrice,
    );

    const boundedItems = orderBy(
      [
        {
          group_item_title: paddedMinPrice,
          yes_price: 0,
          umsStatus: undefined,
          color: items[0]?.color || 'rgba(71, 51, 125, .1)',
        },
        ...items,
        {
          group_item_title: paddedMaxPrice,
          yes_price: 0,
          umsStatus: undefined,
          color: items.at(-1)?.color || 'rgba(71, 51, 125, .1)',
        },
      ],
      'group_item_title',
    );

    return boundedItems.slice(1).map((val, i) => {
      const lowerItem = boundedItems[i];
      const labelItem = lowerItem.group_item_title === paddedMinPrice ? val : lowerItem;
      const areaMeta: MarkAreaMeta = {
        lowerBoundary: lowerItem.group_item_title,
        upperBoundary: val.group_item_title,
        outcomeYesPrice:
          labelItem.umsStatus === 'resolved'
            ? `${labelItem.yes_price}%`
            : +labelItem.yes_price < 1
              ? '<1%'
              : `${labelItem.yes_price}%`,
      };

      return [
        {
          name: lowerItem.group_item_title,
          yAxis: lowerItem.group_item_title,
          areaMeta,
          itemStyle: {
            color: lowerItem.color,
          },
        },
        {
          // name: val.group_item_title,
          yAxis: val.group_item_title,
          areaMeta,
          itemStyle: {
            color: lowerItem.color,
          },
          label: {
            position: 'insideLeft',
            formatter: () => {
              if (labelItem.umsStatus === 'resolved') {
                return `${labelItem.yes_price}%`;
              }
              return +labelItem.yes_price < 1 ? '<1%' : `${labelItem.yes_price}%`;
            },
          },
        },
      ];
    });
  }, [filteredMarkets, klineData?.history]);

  const chartPayload = useMemo(() => {
    if (!markAreaData.length || !klineData?.history) return null;

    const minCryptoPrice = Number(markAreaData[0][0].yAxis);
    const maxCryptoPrice = Number(markAreaData.at(-1)?.[1].yAxis || 0);
    const cryptoPricePoints = klineData.history
      .map<CryptoPricePoint>((item) => ({
        timestampMs: Number(item.close_time) * 1000,
        price: Number(item.close_price),
      }))
      .filter((item) => Number.isFinite(item.timestampMs) && Number.isFinite(item.price))
      .sort((a, b) => a.timestampMs - b.timestampMs);
    const markAreaMetas = markAreaData
      .map((area) => area[0]?.areaMeta)
      .filter((item): item is MarkAreaMeta => Boolean(item));

    const cryptoSeries: echarts.LineSeriesOption = {
      data: cryptoPricePoints.map((item) => {
        return [new Date(item.timestampMs).toISOString(), item.price];
      }),
      name: cryptoName,
      type: 'line',
      showSymbol: false,
      smooth: true,
      yAxisIndex: 0,
      itemStyle: { color: '#FFBE49' },
      markArea: {
        silent: true,
        emphasis: {
          disabled: true,
        },
        data: [...markAreaData] as any,
      },

      lineStyle: { width: 1.5 },
    };

    const option: echarts.EChartsOption = {
      ...BaseChartOptions,
      xAxis: [
        {
          ...xAxis,
          type: 'time',
          axisLabel: { color: '#999', formatter: (v: any) => `${dayjs(v).format('MMM DD')}` },
        } as echarts.XAXisComponentOption,
      ],
      yAxis: [
        {
          ...yAxis,
          position: 'right',
          // startValue: 2300,
          name: `${crypto2Symbol[cryptoName]} Price`,
          min: minCryptoPrice, //Math.floor(minCryptoPrice - minCryptoPrice * 0.01),
          max: maxCryptoPrice, // Math.floor(maxCryptoPrice + maxCryptoPrice * 0.01),
          splitLine: {
            show: true,
            lineStyle: { color: theme === 'dark' ? '#2c2c2f' : '#e9e9e9', type: 'dashed' },
          },
          axisLabel: { color: '#999', formatter: (v: any) => `${currencyFormatter(v)}` },
        },
        {
          type: 'category',
          name: 'probability',
          nameTextStyle: { align: 'left' },
          axisLabel: { show: false },
        },
      ],
      grid,
      tooltip: {
        ...tooltip,
        show: false,
      },
      series: [cryptoSeries],
    };

    return {
      option,
      cryptoPricePoints,
      markAreaMetas,
    };
  }, [cryptoName, klineData?.history, markAreaData, theme]);

  const handleChartReady = useCallback(
    (chart: echarts.ECharts, chartEl: HTMLDivElement) => {
      if (!chartPayload) return;
      chartInstanceRef.current = chart;

      const { cryptoPricePoints, markAreaMetas } = chartPayload;

      const hideTooltip = () => {
        if (!tooltipRef.current) return;
        tooltipRef.current.style.opacity = '0';
        tooltipRef.current.style.pointerEvents = 'none';
        [verticalGuideRef.current, cursorPointRef.current, linePointRef.current].forEach((item) => {
          if (!item) return;
          item.style.opacity = '0';
        });
      };

      const handleMouseMove = (event: { offsetX: number; offsetY: number }) => {
        if (!chart.containPixel({ gridIndex: 0 }, [event.offsetX, event.offsetY])) {
          hideTooltip();
          return;
        }

        const dataPoint = chart.convertFromPixel({ gridIndex: 0 }, [
          event.offsetX,
          event.offsetY,
        ]) as [number, number] | undefined;
        const hoverTime = Number(dataPoint?.[0]);
        const hoverPrice = Number(dataPoint?.[1]);
        if (!Number.isFinite(hoverTime) || !Number.isFinite(hoverPrice)) {
          hideTooltip();
          return;
        }

        const meta = markAreaMetas.find(
          (item) => hoverPrice >= item.lowerBoundary && hoverPrice <= item.upperBoundary,
        );
        const nearestPoint = findNearestPricePoint(cryptoPricePoints, hoverTime);
        const tooltipEl = tooltipRef.current;

        if (!meta || !nearestPoint || !tooltipEl) {
          hideTooltip();
          return;
        }

        tooltipEl.innerHTML = formatMouseTooltip(meta, nearestPoint, crypto2Symbol[cryptoName]);
        tooltipEl.style.opacity = '1';
        tooltipEl.style.pointerEvents = 'none';

        const left = Math.min(event.offsetX + 12, Math.max(12, chartEl.clientWidth - 220));
        const top = Math.max(8, event.offsetY - 96);
        tooltipEl.style.transform = `translate(${left}px, ${top}px)`;

        const linePoint = chart.convertToPixel({ gridIndex: 0 }, [
          nearestPoint.timestampMs,
          nearestPoint.price,
        ]) as [number, number] | undefined;

        updateHoverGuides({
          cursorX: event.offsetX,
          cursorY: event.offsetY,
          lineX: Number(linePoint?.[0]),
          lineY: Number(linePoint?.[1]),
          chartHeight: chartEl.clientHeight,
          verticalGuideEl: verticalGuideRef.current,
          cursorPointEl: cursorPointRef.current,
          linePointEl: linePointRef.current,
        });
      };

      chart.getZr().on('mousemove', handleMouseMove);
      chart.getZr().on('globalout', hideTooltip);

      return () => {
        chart.getZr().off('mousemove', handleMouseMove);
        chart.getZr().off('globalout', hideTooltip);
        if (chartInstanceRef.current === chart) {
          chartInstanceRef.current = null;
        }
      };
    },
    [chartPayload, cryptoName],
  );

  useEffect(() => {
    if (isOpen || chatId || isNotificationOpen) {
      setTimeout(() => {
        chartInstanceRef.current?.resize();
      }, 300);
    }
  }, [isOpen, chatId, isNotificationOpen]);

  return (
    <div className="relative">
      <EChart
        option={chartPayload?.option ?? null}
        empty={!chartPayload}
        className="w-full min-h-65"
        openInSidebar={openInSidebar}
        onChartReady={handleChartReady}
      />
      <div
        ref={tooltipRef}
        className="pointer-events-none absolute left-0 top-0 z-10 rounded border border-[#4D4D4D] bg-[rgba(39,39,39,0.94)] px-3 py-2 text-xs leading-5 text-white opacity-0 shadow-lg transition-opacity"
      />
      <div
        ref={verticalGuideRef}
        className="pointer-events-none absolute top-0 z-[9] w-px border-l border-dashed border-white/45 opacity-0"
      />
      <div
        ref={cursorPointRef}
        className="pointer-events-none absolute left-0 top-0 z-[11] size-2.5 rounded-full bg-white opacity-0 shadow-[0_0_0_2px_rgba(0,0,0,0.55)]"
      />
      <div
        ref={linePointRef}
        className="pointer-events-none absolute left-0 top-0 z-[11] size-2.5 rounded-full bg-[#FFBE49] opacity-0 shadow-[0_0_0_2px_rgba(0,0,0,0.55)]"
      />

      <ResolvedButtons
        window={currentWindow}
        onChange={function (timestamp: number): void {
          throw new Error('Function not implemented.');
        }}
      />
    </div>
  );
}

function formatMouseTooltip(meta: MarkAreaMeta, point: CryptoPricePoint, symbol: string) {
  return [
    `<div style="font-weight:600;margin-bottom:4px;">${dayjs(point.timestampMs).format('MMM DD, HH:mm')}</div>`,
    `<div>${symbol} Price: ${currencyFormatter(point.price, { notation: 'standard' })}</div>`,
    `<div>Upper: ${currencyFormatter(meta.upperBoundary, { notation: 'standard' })}</div>`,
    `<div>Lower: ${currencyFormatter(meta.lowerBoundary, { notation: 'standard' })}</div>`,
    `<div>YES Price: ${meta.outcomeYesPrice}</div>`,
  ].join('');
}

function updateHoverGuides({
  cursorX,
  cursorY,
  lineX,
  lineY,
  chartHeight,
  verticalGuideEl,
  cursorPointEl,
  linePointEl,
}: {
  cursorX: number;
  cursorY: number;
  lineX: number;
  lineY: number;
  chartHeight: number;
  verticalGuideEl: HTMLDivElement | null;
  cursorPointEl: HTMLDivElement | null;
  linePointEl: HTMLDivElement | null;
}) {
  if (verticalGuideEl) {
    verticalGuideEl.style.opacity = '1';
    verticalGuideEl.style.height = `${chartHeight}px`;
    verticalGuideEl.style.transform = `translateX(${cursorX}px)`;
  }

  if (cursorPointEl) {
    cursorPointEl.style.opacity = '1';
    cursorPointEl.style.transform = `translate(${cursorX - 5}px, ${cursorY - 5}px)`;
  }

  if (linePointEl && Number.isFinite(lineX) && Number.isFinite(lineY)) {
    linePointEl.style.opacity = '1';
    linePointEl.style.transform = `translate(${lineX - 5}px, ${lineY - 5}px)`;
  } else if (linePointEl) {
    linePointEl.style.opacity = '0';
  }
}

function findNearestPricePoint(points: CryptoPricePoint[], timestampMs: number) {
  if (!points.length) return null;

  let left = 0;
  let right = points.length - 1;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (points[mid].timestampMs < timestampMs) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  const current = points[left];
  const previous = points[left - 1];
  if (!previous) return current;
  if (!current) return previous;

  return Math.abs(current.timestampMs - timestampMs) < Math.abs(previous.timestampMs - timestampMs)
    ? current
    : previous;
}

function confirmOutcome(market: PolymarketMarket) {
  if (market.uma_resolution_status === 'resolved' || market.market_closed) {
    return parseFloat(market.outcome_yes_price) > 0.5 ? '100%' : '0%';
  }
  return `${(+market.outcome_yes_price * 100).toFixed(1)}%`;
}

function confirmLastProbability(market: PolymarketMarket, probability: number) {
  if (market.uma_resolution_status === 'resolved' || market.market_closed) {
    return market.outcome_yes_price;
  }
  return probability;
}
